import React, { useMemo, useState, forwardRef } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import { Eye, EyeOff } from "react-feather";
import { RequiredSign } from "../../Constant/common";

const BaseInput = forwardRef(
  (
    {
      label,
      name,
      type,
      className,
      placeholder,
      handleChange,
      handleBlur,
      value,
      touched,
      error,
      step,
      maxLength,
      icon,
      disabled,
      accept,
      required,
      max,
      min,
      rows,
    },
    ref,
  ) => {
    const [passwordShow, setPasswordShow] = useState(false);

    const phoneValue = useMemo(() => {
      if (value === null || value === undefined) return "";
      const v = String(value);
      return v.startsWith("+") ? v.slice(1) : v;
    }, [value]);

    const isInvalid = !!(touched && error);

    const handleKeyInput = (event) => {
      const allowedKeys = [
        "Backspace",
        "Delete",
        "ArrowLeft",
        "ArrowRight",
        "ArrowUp",
        "ArrowDown",
        "Tab",
      ];
      if (type === "number" && ["e", "E", "+", "-"].includes(event.key)) event.preventDefault();
      if (type === "number" && event.code === "Space") event.preventDefault();
      if (type === "email" && event.code === "Space") event.preventDefault();
      if (type === "number" && !allowedKeys.includes(event.key) && !event.key.match(/^[0-9]$/))
        event.preventDefault();
    };

    const inputClass = `form-control${isInvalid ? " is-invalid" : ""} ${className || ""}`.trim();

    return (
      <div className="mb-3">
        {label && (
          <label htmlFor={name} className="form-label">
            {label}
            {required && <span className="text-danger ms-1">{RequiredSign}</span>}
          </label>
        )}

        {type === "file" ? (
          <input
            name={name}
            id={name}
            type="file"
            className={inputClass}
            onChange={handleChange}
            disabled={disabled}
            ref={ref}
            accept={accept}
          />
        ) : type === "phone" ? (
          <PhoneInput
            country="in"
            value={phoneValue}
            onChange={(val) => {
              const formatted = val ? `+${val}` : "";
              if (typeof handleChange === "function")
                handleChange({ target: { name, value: formatted } });
            }}
            onBlur={() => {
              if (typeof handleBlur === "function") handleBlur({ target: { name } });
            }}
            inputProps={{ name, id: name, disabled, placeholder }}
            inputClass={`form-control w-100${isInvalid ? " is-invalid" : ""}`}
            inputStyle={{ paddingLeft: 58 }}
            buttonStyle={{ width: 48 }}
            buttonClass={disabled ? "disabled" : ""}
            containerClass="w-100"
            enableSearch
          />
        ) : type === "textarea" ? (
          <textarea
            name={name}
            id={name}
            value={value ?? ""}
            className={`${inputClass} font-monospace fs-7 lh-sm rounded-2`}
            placeholder={placeholder}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={disabled}
            rows={rows || 5}
            style={{
              resize: "vertical",
            }}
            ref={(el) => {
              if (ref) {
                typeof ref === "function" ? ref(el) : (ref.current = el);
              }
              if (el) {
                el.style.setProperty("height", "auto", "important");
                el.style.setProperty("min-height", "220px", "important");
              }
            }}
          />
        ) : type === "password" ? (
          <div style={{ position: "relative" }}>
            {icon && (
              <span
                style={{
                  position: "absolute",
                  left: 12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "var(--text-muted)",
                  display: "flex",
                  alignItems: "center",
                  zIndex: 1,
                }}
              >
                {icon}
              </span>
            )}
            <input
              name={name}
              id={name}
              value={value ?? ""}
              type={passwordShow ? "text" : "password"}
              className={inputClass}
              placeholder={placeholder}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={disabled}
              style={{
                paddingLeft: icon ? 36 : undefined,
                paddingRight: 40,
              }}
            />
            <button
              type="button"
              className="password-toggle-btn"
              onClick={() => setPasswordShow((p) => !p)}
              aria-label={passwordShow ? "Hide password" : "Show password"}
              tabIndex={-1}
              style={{
                position: "absolute",
                right: 10,
                top: "50%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "var(--text-muted)",
                display: "flex",
                alignItems: "center",
                padding: 2,
                transition: "color 0.15s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = "var(--brand-indigo)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = "var(--text-muted)";
              }}
            >
              {passwordShow ? (
                <EyeOff size={15} strokeWidth={2} aria-hidden="true" />
              ) : (
                <Eye size={15} strokeWidth={2} aria-hidden="true" />
              )}
            </button>
          </div>
        ) : (
          <div style={{ position: "relative" }}>
            {icon && (
              <span
                style={{
                  position: "absolute",
                  left: 12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "var(--text-muted)",
                  display: "flex",
                  alignItems: "center",
                  zIndex: 1,
                  pointerEvents: "none",
                }}
              >
                {icon}
              </span>
            )}
            <input
              name={name}
              id={name}
              type={type || "text"}
              className={inputClass}
              placeholder={placeholder}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={disabled}
              value={value ?? ""}
              onKeyDown={handleKeyInput}
              min={min}
              step={step}
              maxLength={maxLength}
              max={max}
              ref={(el) => {
                // Forward ref to parent if provided
                if (ref) {
                  typeof ref === "function" ? ref(el) : (ref.current = el);
                }
                if (el && icon) el.style.setProperty("padding-left", "36px", "important");
              }}
            />
          </div>
        )}

        {isInvalid && (
          <div className="invalid-feedback d-block" id="baseInput-err">
            {error}
          </div>
        )}
      </div>
    );
  },
);

BaseInput.displayName = "BaseInput";

export default BaseInput;
